import React, { useState, useEffect } from 'react';
import { ArrowRight, Target, Lightbulb, Users, CheckCircle2, Wrench, Box, Warehouse, HardHat, Cog, Phone, Mail, MapPin, ChevronDown, Award, TrendingUp, Shield, Zap, Brain, Compass, Eye, Rocket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPageV2 = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [quoteForm, setQuoteForm] = useState({ companyName: '', email: '', phone: '', service: '', projectDetails: '', timeline: '' });
  const [contactLoading, setContactLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'philosophy', 'principles', 'values', 'services', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element && scrollPosition >= element.offsetTop && scrollPosition < element.offsetTop + element.offsetHeight) {
          setActiveSection(section);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    try {
      const response = await axios.post(`${API_URL}/contact`, contactForm);
      toast.success(response.data.message || 'Message sent successfully! We\'ll get back to you soon.');
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      let errorMessage;
      if (Array.isArray(error.response?.data?.detail)) {
        // FastAPI validation errors return an array
        errorMessage = error.response.data.detail.map(e => e.msg).join(', ');
      } else {
        errorMessage = error.response?.data?.detail || error.response?.data?.error || 'Failed to send message. Please try again.';
      }
      toast.error(errorMessage);
    } finally {
      setContactLoading(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setQuoteLoading(true);
    try {
      const response = await axios.post(`${API_URL}/quote`, quoteForm);
      toast.success(response.data.message || 'Quote request submitted! Our team will contact you shortly.');
      setQuoteForm({ companyName: '', email: '', phone: '', service: '', projectDetails: '', timeline: '' });
    } catch (error) {
      console.error('Quote form error:', error);
      let errorMessage;
      if (Array.isArray(error.response?.data?.detail)) {
        // FastAPI validation errors return an array
        errorMessage = error.response.data.detail.map(e => e.msg).join(', ');
      } else {
        errorMessage = error.response?.data?.detail || error.response?.data?.error || 'Failed to submit quote request. Please try again.';
      }
      toast.error(errorMessage);
    } finally {
      setQuoteLoading(false);
    }
  };

  const jdiMindset = [
  {
    icon: Brain,
    title: 'Question Assumptions',
    description: 'Innovation begins where conventional thinking ends.',
    highlight: 'Challenge the status quo and explore better ways.'
  },
  {
    icon: Target,
    title: 'Embrace Challenges',
    description: 'Every obstacle presents an opportunity to improve.',
    highlight: 'Problems create fear. Challenges create innovation.'
  },
  {
    icon: Zap,
    title: 'Simplify Complexity',
    description: 'The best solutions are often the simplest.',
    highlight: 'We engineer clarity from complexity through intelligent design.'
  },
  {
    icon: CheckCircle2,
    title: 'Deliver Outcomes',
    description: 'Results matter more than activity.',
    highlight: 'Being busy is not the same as being productive.'
  },
  {
    icon: Award,
    title: 'Pursue Excellence',
    description: 'Excellence is earned through consistency and discipline.',
    highlight: 'We set ambitious goals and hold ourselves accountable.'
  },
  {
    icon: Users,
    title: 'Empower People',
    description: 'Great achievements are built by capable and trusted teams.',
    highlight: 'The people closest to the work have the best solutions.'
  },
  {
    icon: TrendingUp,
    title: 'Evolve Continuously',
    description: 'Growth comes from learning, adapting, and improving.',
    highlight: 'Transformation is not an event. It is a continuous journey.'
  },
  {
    icon: Compass,
    title: 'Build Together',
    description: 'Long-term success is founded on strong partnerships.',
    highlight: 'Trust and mutual success drive lasting relationships.'
  }];


  const services = [
  {
    icon: Cog,
    title: 'Electrical Assembly',
    description: 'Precision electrical assembly services with meticulous attention to detail, ensuring reliability and performance in every connection.'
  },
  {
    icon: Wrench,
    title: 'Control Panel Integration',
    description: 'Custom control panel design and integration for industrial automation, delivering seamless operation and optimal efficiency.'
  },
  {
    icon: Box,
    title: 'Wire Harness Manufacturing',
    description: 'High-quality wire harness fabrication tailored to your specifications, meeting the most demanding industrial standards.'
  },
  {
    icon: HardHat,
    title: 'Industrial Wiring',
    description: 'Comprehensive industrial wiring solutions for OEMs, machine builders, and system integrators across all sectors.'
  },
  {
    icon: Box,
    title: 'Box Build Projects',
    description: 'Complete electro-mechanical assembly services from concept to completion, integrating hardware and software seamlessly.'
  },
  {
    icon: Warehouse,
    title: 'Warehousing & Support',
    description: 'Flexible warehousing and dedicated labour support to scale with your production needs and timelines.'
  }];


  return (
    <div className="landing-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex flex-col">
              <span className="text-3xl font-bold tracking-tight text-orange-600">JDI</span>
              <span className="text-xs font-semibold text-gray-800 tracking-wide">AUTOMATION SDN BHD</span>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            {['Home', 'Philosophy', 'Mindset', 'Values', 'Services', 'Contact'].map((item) =>
            <button
              key={item}
              onClick={() => scrollToSection(item.toLowerCase() === 'home' ? 'hero' : item.toLowerCase() === 'mindset' ? 'principles' : item.toLowerCase())}
              className={`text-sm font-medium transition-colors hover:text-orange-600 ${
              activeSection === (item.toLowerCase() === 'home' ? 'hero' : item.toLowerCase() === 'mindset' ? 'principles' : item.toLowerCase()) ?
              'text-orange-600' :
              'text-gray-700'}`
              }>

                {item}
              </button>
            )}
          </nav>
          <Button onClick={() => scrollToSection('contact')} className="bg-orange-600 hover:bg-orange-700 text-white">
            Get Started
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1647427060118-4911c9821b82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwYXV0b21hdGlvbnxlbnwwfHx8fDE3ODEwNjk2MDJ8MA&ixlib=rb-4.1.0&q=85"
            alt="Industrial automation robotic arm in modern manufacturing facility - JDI Automation precision engineering"
            className="w-full h-full object-cover"
            loading="eager"
          />

          <div className="absolute inset-0 bg-gradient-to-r from-orange-600/95 via-orange-500/85 to-transparent"></div>
        </div>

        {/* Geometric Shapes */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-orange-400/20 rounded-full blur-3xl"></div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-white">
          <div className="max-w-3xl">
            <div className="inline-block mb-6 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
              <span className="text-sm font-semibold">Journey Together, Drive Excellence, Innovate Fearlessly</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Others See Problems.<br />
              <span className="text-white/90">We See Challenges.</span>
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-white/95 font-medium">
              Others Wait. We Act.
            </p>
            <p className="text-lg mb-8 text-white/90 max-w-2xl leading-relaxed">
              At JDI Automation, every challenge represents an opportunity to innovate, improve, and create value. 
              We challenge conventional thinking, embrace technology, and execute with discipline to deliver solutions that generate real business results.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => scrollToSection('contact')}
                size="lg"
                className="bg-white text-orange-600 hover:bg-gray-100 font-semibold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all">

                Get a Quote <ArrowRight className="ml-2" />
              </Button>
              <Button
                onClick={() => scrollToSection('philosophy')}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-orange-600 font-semibold px-8 py-6 text-lg backdrop-blur-sm transition-all">

                Our Philosophy
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <button
          onClick={() => scrollToSection('philosophy')}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/80 hover:text-white transition-all animate-bounce">

          <ChevronDown size={32} />
        </button>
      </section>

      {/* Philosophy Section */}
      <section id="philosophy" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-orange-100 rounded-full">
              <span className="text-sm font-semibold text-orange-600">OUR PHILOSOPHY</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Who We Are
            </h2>
            <div className="max-w-4xl mx-auto space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                JDI Automation was founded on a simple belief: <span className="font-semibold text-gray-900">Innovation without execution creates ideas. 
                Execution without innovation creates stagnation.</span>
              </p>
              <p>
                We combine both.
              </p>
              <p>
                By challenging conventional thinking, embracing technology, and focusing relentlessly on measurable outcomes, 
                we help organizations overcome operational challenges and unlock new levels of efficiency, productivity, and growth.
              </p>
              <p className="text-xl font-semibold text-orange-600">
                Inspired by the principles of innovation, accountability, and continuous improvement, we partner with our customers 
                to transform challenges into opportunities and opportunities into results.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mt-16">
            <Card className="border-2 border-orange-500 shadow-xl">
              <CardContent className="p-8">
                <Eye className="w-12 h-12 text-orange-600 mb-4" />
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Vision</h3>
                <p className="text-gray-700 leading-relaxed">
                  To build a world where efficiency is engineered, innovation is embraced, 
                  and every challenge becomes a stepping stone to progress.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-500 shadow-xl">
              <CardContent className="p-8">
                <Target className="w-12 h-12 text-orange-600 mb-4" />
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Mission</h3>
                <p className="text-gray-700 leading-relaxed">
                  To transform challenges into measurable outcomes through disciplined execution, 
                  continuous improvement, and innovative engineering.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* JDI Mindset Section */}
      <section id="principles" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-orange-100 rounded-full">
              <span className="text-sm font-semibold text-orange-600">THE JDI MINDSET</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Principles That Drive Us Forward
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our mindset combines innovation with execution, balancing bold thinking with disciplined action 
              to transform challenges into measurable outcomes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {jdiMindset.map((principle, index) => {
              const Icon = principle.icon;
              return (
                <Card key={index} className="border border-gray-200 hover:border-orange-500 transition-all hover:shadow-xl group bg-white">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-orange-100 group-hover:bg-orange-600 rounded-xl flex items-center justify-center mb-4 transition-colors">
                      <Icon className="w-7 h-7 text-orange-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-lg font-bold mb-3 text-gray-900 group-hover:text-orange-600 transition-colors">
                      {principle.title}
                    </h3>
                    <p className="text-gray-600 mb-3 text-sm leading-relaxed">{principle.description}</p>
                    <p className="text-xs text-orange-600 italic font-medium">"{principle.highlight}"</p>
                  </CardContent>
                </Card>);

            })}
          </div>

          <div className="mt-16 text-center">
            <Card className="bg-gradient-to-br from-orange-500 to-orange-600 border-none shadow-2xl">
              <CardContent className="p-12">
                <h3 className="text-3xl font-bold text-white mb-4">Dream Boldly. Execute Relentlessly.</h3>
                <p className="text-xl text-white/90">
                  Where innovation meets execution, challenges become opportunities.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values (J-D-I) Section */}
      <section id="values" className="py-20 bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-400 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <span className="text-sm font-semibold">THE JDI WAY</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Our Core Values</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              J.D.I. - More than an acronym. It's our commitment to transforming challenges into opportunities.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow bg-white text-gray-900">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
                  <Users className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-2xl font-bold mb-4">J - Journey Together</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We grow alongside our customers and partners, building long-term relationships based on trust and mutual success.
                </p>
                <p className="text-sm text-orange-600 font-semibold">
                  Empower people. Trust expertise. Collaborate for shared success.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-br from-orange-500 to-orange-600 text-white transform md:scale-105">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4">D - Drive Excellence</h3>
                <p className="leading-relaxed opacity-95 mb-4">
                  We pursue excellence in everything we do, continuously improving our processes, solutions, and performance.
                </p>
                <p className="text-sm font-semibold opacity-95">
                  Focus on outcomes. Hold ourselves accountable. Deliver measurable results.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow bg-white text-gray-900">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
                  <Lightbulb className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-2xl font-bold mb-4">I - Innovate Fearlessly</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We challenge the status quo and embrace innovation to create smarter, more efficient ways of working.
                </p>
                <p className="text-sm text-orange-600 font-semibold">
                  Challenge convention. Embrace technology. Simplify complexity.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <h3 className="text-3xl font-bold mb-4">True North</h3>
            <p className="text-2xl text-orange-500 font-semibold mb-8">
              Transforming Challenges Into Opportunities Through Innovation and Action
            </p>
            <div className="flex justify-center items-center gap-4">
              <div className="text-center">
                <Users className="w-10 h-10 mx-auto mb-2 text-orange-500" />
                <p className="text-sm font-semibold">Journey<br />Together</p>
              </div>
              <ArrowRight className="w-8 h-8 text-orange-500" />
              <div className="text-center">
                <Target className="w-10 h-10 mx-auto mb-2 text-orange-500" />
                <p className="text-sm font-semibold">Drive<br />Excellence</p>
              </div>
              <ArrowRight className="w-8 h-8 text-orange-500" />
              <div className="text-center">
                <Lightbulb className="w-10 h-10 mx-auto mb-2 text-orange-500" />
                <p className="text-sm font-semibold">Innovate<br />Fearlessly</p>
              </div>
              <ArrowRight className="w-8 h-8 text-orange-500" />
              <div className="text-center">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-orange-500" />
                <p className="text-sm font-semibold">Deliver<br />Results</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-orange-100 rounded-full">
              <span className="text-sm font-semibold text-orange-600">WHAT WE DO</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive automation solutions tailored to your industrial needs, from concept to completion.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border border-gray-200 hover:border-orange-500 transition-all hover:shadow-xl group">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-orange-100 group-hover:bg-orange-600 rounded-xl flex items-center justify-center mb-6 transition-colors">
                      <Icon className="w-7 h-7 text-orange-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-gray-900 group-hover:text-orange-600 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>);

            })}
          </div>
        </div>
      </section>

      {/* Contact & Quote Section */}
      <section id="contact" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-orange-100 rounded-full">
              <span className="text-sm font-semibold text-orange-600">GET IN TOUCH</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">Let's Work Together</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to transform your challenges into opportunities? Get in touch with our team today.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Contact Form */}
            <Card className="border-none shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Send Us a Message</h3>
                <form onSubmit={handleContactSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="contact-name">Name *</Label>
                    <Input
                      id="contact-name"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="mt-1"
                      placeholder="Your full name" />

                  </div>
                  <div>
                    <Label htmlFor="contact-email">Email *</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="mt-1"
                      placeholder="your@email.com" />

                  </div>
                  <div>
                    <Label htmlFor="contact-phone">Phone</Label>
                    <Input
                      id="contact-phone"
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      className="mt-1"
                      placeholder="+60 12-345 6789" />

                  </div>
                  <div>
                    <Label htmlFor="contact-message">Message *</Label>
                    <Textarea
                      id="contact-message"
                      required
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="mt-1 min-h-[120px]"
                      placeholder="Tell us about your project..." />

                  </div>
                  <Button
                    type="submit"
                    disabled={contactLoading}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg font-semibold">

                    {contactLoading ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Quote Request Form */}
            <Card className="border-none shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Request a Quote</h3>
                <form onSubmit={handleQuoteSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="quote-company">Company Name *</Label>
                    <Input
                      id="quote-company"
                      required
                      value={quoteForm.companyName}
                      onChange={(e) => setQuoteForm({ ...quoteForm, companyName: e.target.value })}
                      className="mt-1"
                      placeholder="Your company name" />

                  </div>
                  <div>
                    <Label htmlFor="quote-email">Email *</Label>
                    <Input
                      id="quote-email"
                      type="email"
                      required
                      value={quoteForm.email}
                      onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                      className="mt-1"
                      placeholder="business@company.com" />

                  </div>
                  <div>
                    <Label htmlFor="quote-phone">Phone *</Label>
                    <Input
                      id="quote-phone"
                      type="tel"
                      required
                      value={quoteForm.phone}
                      onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                      className="mt-1"
                      placeholder="+60 12-345 6789" />

                  </div>
                  <div>
                    <Label htmlFor="quote-service">Service Interested In *</Label>
                    <Select
                      required
                      value={quoteForm.service}
                      onValueChange={(value) => setQuoteForm({ ...quoteForm, service: value })}>

                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="electrical-assembly">Electrical Assembly</SelectItem>
                        <SelectItem value="control-panel">Control Panel Integration</SelectItem>
                        <SelectItem value="wire-harness">Wire Harness Manufacturing</SelectItem>
                        <SelectItem value="industrial-wiring">Industrial Wiring</SelectItem>
                        <SelectItem value="box-build">Box Build Projects</SelectItem>
                        <SelectItem value="warehousing">Warehousing & Support</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="quote-details">Project Details *</Label>
                    <Textarea
                      id="quote-details"
                      required
                      value={quoteForm.projectDetails}
                      onChange={(e) => setQuoteForm({ ...quoteForm, projectDetails: e.target.value })}
                      className="mt-1 min-h-[80px]"
                      placeholder="Brief description of your project" />

                  </div>
                  <div>
                    <Label htmlFor="quote-timeline">Timeline</Label>
                    <Input
                      id="quote-timeline"
                      value={quoteForm.timeline}
                      onChange={(e) => setQuoteForm({ ...quoteForm, timeline: e.target.value })}
                      className="mt-1"
                      placeholder="e.g., 2-3 months" />

                  </div>
                  <Button
                    type="submit"
                    disabled={quoteLoading}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg font-semibold">

                    {quoteLoading ? 'Submitting...' : 'Request Quote'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <div className="mt-16 grid md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-orange-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Phone</h4>
              <p className="text-gray-600">014-603 7996</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-orange-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Email</h4>
              <p className="text-gray-600">sales@jdiautomation.com</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-orange-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Address</h4>
              <p className="text-gray-600 text-sm">NO. 17-2-5 BAYAN POINT, MEDAN KAMPUNG RELAU MK12,<br />11900 BAYAN LEPAS, PENANG</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <div className="flex flex-col items-center md:items-start">
                <span className="text-3xl font-bold tracking-tight text-orange-600">JDI</span>
                <span className="text-xs font-semibold text-gray-400 tracking-wide">AUTOMATION SDN BHD</span>
                <p className="text-sm text-gray-400 mt-2">Journey Together, Drive Excellence, Innovate Fearlessly.</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-gray-400 text-sm mb-2">Challenge. Innovate. Evolve.</p>
              <p className="text-gray-500 text-sm">© 2026 JDI Automation Sdn Bhd. All rights reserved.</p>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center">
            <p className="text-sm text-gray-500 mb-4">
              Innovation without execution creates ideas. Execution without innovation creates stagnation.
            </p>
            <p className="text-sm text-gray-400">
              Together We <span className="text-orange-600 font-semibold">DRIVE</span>. Together We <span className="text-orange-600 font-semibold">INNOVATE</span>. Together We <span className="text-orange-600 font-semibold">SUCCEED</span>.
            </p>
          </div>
        </div>
      </footer>
    </div>);

};

export default LandingPageV2;